export interface KcAttempt {
  choiceId: string
  correct: boolean
  firstAttemptAt: string
  lastAttemptAt?: string
}

export type KcAttemptsMap = Record<string, KcAttempt>

function storageKey(moduleId: string): string {
  return `kc-attempts:${moduleId}`
}

export function getKcAttempts(moduleId: string): KcAttemptsMap {
  try {
    const raw = localStorage.getItem(storageKey(moduleId))
    if (!raw) return {}
    const parsed = JSON.parse(raw) as KcAttemptsMap
    if (!parsed || typeof parsed !== 'object') return {}
    return parsed
  } catch {
    return {}
  }
}

export interface RecordKcAttemptResult {
  attempt: KcAttempt
  isFirst: boolean
}

export function recordKcAttempt(
  moduleId: string,
  kcId: string,
  choiceId: string,
  correct: boolean,
): RecordKcAttemptResult {
  const now = new Date().toISOString()
  const existing = getKcAttempts(moduleId)
  const prior = existing[kcId]
  const isFirst = !prior
  const attempt: KcAttempt = prior
    ? { ...prior, lastAttemptAt: now }
    : { choiceId, correct, firstAttemptAt: now }
  const merged: KcAttemptsMap = { ...existing, [kcId]: attempt }
  try {
    localStorage.setItem(storageKey(moduleId), JSON.stringify(merged))
  } catch {
    // Safari private mode + quota errors — ignore
  }
  return { attempt, isFirst }
}

export interface ServerKcAttempt {
  kcId: string | null
  choiceId: string | null
  correct: boolean
  createdAt: string | Date | null
}

// Merges server-side attempts into localStorage without overwriting newer
// client state. Returns true if any local entry was added.
export function mergeServerKcAttempts(moduleId: string, serverAttempts: ServerKcAttempt[]): boolean {
  const existing = getKcAttempts(moduleId)
  let changed = false
  for (const s of serverAttempts) {
    if (!s.kcId || !s.choiceId) continue
    if (existing[s.kcId]) continue
    const createdAt =
      s.createdAt instanceof Date
        ? s.createdAt.toISOString()
        : typeof s.createdAt === 'string' && s.createdAt.length > 0
        ? s.createdAt
        : new Date().toISOString()
    existing[s.kcId] = {
      choiceId: s.choiceId,
      correct: s.correct,
      firstAttemptAt: createdAt,
    }
    changed = true
  }
  if (changed) {
    try {
      localStorage.setItem(storageKey(moduleId), JSON.stringify(existing))
    } catch {
      // ignore
    }
  }
  return changed
}

export function hasAttemptedAll(moduleId: string, kcIds: string[]): boolean {
  if (kcIds.length === 0) return true
  const attempts = getKcAttempts(moduleId)
  return kcIds.every((id) => Boolean(attempts[id]))
}

export const KC_ATTEMPT_EVENT = 'kc-attempt-recorded'

export function emitKcAttemptEvent(moduleId: string, kcId: string): void {
  try {
    window.dispatchEvent(
      new CustomEvent(KC_ATTEMPT_EVENT, { detail: { moduleId, kcId } }),
    )
  } catch {
    // ignore
  }
}
